import React from 'react';

export interface MarkupToken {
  type: 'text' | 'bold' | 'underline' | 'list-item' | 'line-break';
  content: string;
}

export function parseMarkup(text: string): MarkupToken[] {
  const tokens: MarkupToken[] = [];
  const lines = text.split('\n');
  
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    
    // Check if line is a list item (starts with "- ")
    if (line.trim().startsWith('- ')) {
      const listContent = line.trim().substring(2); // Remove "- "
      const parsedContent = parseLineMarkup(listContent);
      tokens.push({ type: 'list-item', content: '' });
      tokens.push(...parsedContent);
    } else {
      // Parse regular line
      const parsedContent = parseLineMarkup(line);
      tokens.push(...parsedContent);
    }
    
    // Add line break if not the last line
    if (lineIndex < lines.length - 1) {
      tokens.push({ type: 'line-break', content: '' });
    }
  }
  
  return tokens;
}

function parseLineMarkup(text: string): MarkupToken[] {
  const tokens: MarkupToken[] = [];
  let i = 0;
  
  while (i < text.length) {
    const char = text[i];
    
    if (char === '*') {
      // Find the closing *
      const endIndex = text.indexOf('*', i + 1);
      if (endIndex !== -1) {
        const content = text.substring(i + 1, endIndex);
        if (content.trim().length > 0) {
          tokens.push({ type: 'bold', content });
          i = endIndex + 1;
          continue;
        }
      }
    } else if (char === '_') {
      // Find the closing _
      const endIndex = text.indexOf('_', i + 1);
      if (endIndex !== -1) {
        const content = text.substring(i + 1, endIndex);
        if (content.trim().length > 0) {
          tokens.push({ type: 'underline', content });
          i = endIndex + 1;
          continue;
        }
      }
    }
    
    // If we reach here, it's regular text
    let textContent = char;
    i++;
    
    // Collect consecutive text characters
    while (i < text.length && text[i] !== '*' && text[i] !== '_') {
      textContent += text[i];
      i++;
    }
    
    if (textContent.length > 0) {
      tokens.push({ type: 'text', content: textContent });
    }
  }
  
  return tokens;
}

export function renderMarkupTokens(tokens: MarkupToken[]): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let currentListItems: React.ReactNode[] = [];
  let elementIndex = 0;
  
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    
    switch (token.type) {
      case 'bold':
        elements.push(
          <strong key={elementIndex++} className="font-bold">
            {token.content}
          </strong>
        );
        break;
      case 'underline':
        elements.push(
          <u key={elementIndex++} className="underline">
            {token.content}
          </u>
        );
        break;
      case 'list-item':
        // Start collecting list item content
        const listItemContent: React.ReactNode[] = [];
        i++; // Move to next token after list-item marker
        
        // Collect tokens until we hit a line-break or end
        while (i < tokens.length && tokens[i].type !== 'line-break') {
          const itemToken = tokens[i];
          switch (itemToken.type) {
            case 'bold':
              listItemContent.push(
                <strong key={`li-${elementIndex++}`} className="font-bold">
                  {itemToken.content}
                </strong>
              );
              break;
            case 'underline':
              listItemContent.push(
                <u key={`li-${elementIndex++}`} className="underline">
                  {itemToken.content}
                </u>
              );
              break;
            case 'text':
              listItemContent.push(
                <span key={`li-${elementIndex++}`}>
                  {itemToken.content}
                </span>
              );
              break;
          }
          i++;
        }
        
        currentListItems.push(
          <li key={`list-item-${elementIndex++}`} className="ml-4 list-disc list-inside">
            {listItemContent}
          </li>
        );
        
        // Check if next token is not a list-item (end of list)
        const nextNonBreakToken = tokens.find((t, idx) => idx > i && t.type !== 'line-break');
        if (!nextNonBreakToken || nextNonBreakToken.type !== 'list-item') {
          // End of list, wrap in ul
          elements.push(
            <ul key={`list-${elementIndex++}`} className="space-y-1 my-2">
              {currentListItems}
            </ul>
          );
          currentListItems = [];
        }
        break;
      case 'line-break':
        elements.push(<br key={elementIndex++} />);
        break;
      case 'text':
      default:
        elements.push(
          <span key={elementIndex++}>
            {token.content}
          </span>
        );
        break;
    }
  }
  
  // Handle any remaining list items
  if (currentListItems.length > 0) {
    elements.push(
      <ul key={`list-final-${elementIndex++}`} className="space-y-1 my-2">
        {currentListItems}
      </ul>
    );
  }
  
  return elements;
}